import {
  documentsDestroyMutation,
  documentsListOptions,
  documentsListQueryKey,
  ReturnDocumentReadable
} from '@crm/api'
import { ConfirmModal, DocumentForm } from '@crm/components'
import { DocumentsFilter } from '@crm/features/documents'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import {
  ConditionalText,
  TableActions,
  TableContainer,
  TitleSection
} from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryUpdate,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { downloadFile } from '@shared/utils'
import { defaultFilters } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Modal, Space, Typography } from 'antd'

import { Document, DocumentDownload, Edit2, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'document-form'

export const Documents = () => {
  const { t } = useLocales()
  const { params } = useQueryUpdate()
  const sorter = useTableSorter()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { resetPagination } = useResetPagination()

  const { message } = App.useApp()

  const [modalState, setModalState] = useState<{
    mode?: 'add' | 'edit' | 'delete' | undefined
    id?: number
  }>({
    mode: undefined
  })

  const onCancel = () => setModalState({ id: undefined })

  const { data: documents, isLoading } = useQuery({
    ...documentsListOptions({
      query: {
        ...defaultFilters,
        ...params
      }
    })
  })

  const { mutate } = useMutation({
    ...documentsDestroyMutation(),
    onSuccess: () => {
      invalidateQueries([documentsListQueryKey()])
      resetPagination()
      onCancel()
      message.success(t('success.success'))
    }
  })

  const onEdit = useCallback((id: number) => {
    setModalState({ mode: 'edit', id })
  }, [])

  const onAdd = () => {
    setModalState({ mode: 'add' })
  }

  const onDelete = () => {
    mutate({ path: { id: Number(modalState.id) } })
  }

  const getActionColumnItem = useCallback(
    (
      document: ReturnDocumentReadable & { file: string }
    ): MenuProps['items'] => [
      {
        label: t('documents.download'),
        key: 'download',
        icon: <DocumentDownload size={20} />,
        onClick: () =>
          downloadFile(
            document?.file ||
              document?.file_legacy_url ||
              document?.attachment?.file ||
              '',
            String(document?.name || document?.attachment?.title)
          )
      },
      {
        label: t('documents.edit'),
        key: 'edit',
        icon: <Edit2 size={20} />,
        onClick: () => onEdit(Number(document?.id))
      },
      { type: 'divider' },
      {
        label: t('documents.delete'),
        key: 'delete',
        icon: <Trash size={20} />,
        danger: true,
        onClick: () => setModalState({ mode: 'delete', id: document?.id })
      }
    ],
    [t, setModalState, onEdit]
  )

  const columns: TableColumnsType<ReturnDocumentReadable> = useMemo(
    () => [
      {
        title: t('documents.name'),
        sorter: true,
        key: 'name',
        render: ({ name, file }) => (
          <Typography.Link
            href={file}
            download={name}
            target="_blank"
            rel="noreferrer"
          >
            <Space>
              <Document size={16} />
              {name}
            </Space>
          </Typography.Link>
        )
      },
      {
        title: t('documents.category'),
        sorter: true,
        dataIndex: ['category', 'name'],
        key: 'category',
        render: (category) => (
          <ConditionalText condition={category}>{category}</ConditionalText>
        )
      },
      {
        title: t('task.lead'),
        sorter: true,
        dataIndex: ['lead', 'name'],
        key: 'lead',
        render: (lead) => (
          <ConditionalText condition={lead}>{lead}</ConditionalText>
        )
      },
      {
        title: t('documents.privacy'),
        sorter: true,
        key: 'is_public',
        render: (_, { is_public }) =>
          t(is_public ? 'documents.public' : 'documents.private')
      },
      {
        width: 50,
        fixed: 'right',
        render: (document: ReturnDocumentReadable & { file: string }) => (
          <TableActions options={getActionColumnItem(document)} />
        )
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <>
      <TitleSection
        title={t(`documents.documents`)}
        count={documents?.count}
        isLoading={isLoading}
      />

      <TableContainer
        rowKey="id"
        rowClassName="pointer"
        columns={columns}
        data={documents}
        loading={isLoading}
        isHoverAlternative
        showSearch
        showSorterTooltip={false}
        leftChildren={<DocumentsFilter />}
        onChange={(_, __, result) => sorter(result)}
        rightChildren={
          <Button type="primary" onClick={onAdd}>
            {t('documents.add_document')}
          </Button>
        }
      />

      <ConfirmModal
        open={!!modalState?.id && modalState.mode === 'delete'}
        onSuccess={onDelete}
        onCancel={onCancel}
      />

      <Modal
        maskClosable={false}
        open={
          (!!modalState?.id && modalState.mode === 'edit') ||
          modalState.mode === 'add'
        }
        title={t(
          modalState.mode === 'add'
            ? 'documents.add_document'
            : 'documents.edit_document'
        )}
        onCancel={onCancel}
        destroyOnHidden
        cancelText={t('buttons.cancel')}
        okText={t('buttons.save')}
        okButtonProps={{
          htmlType: 'submit',
          form: formName
        }}
      >
        <DocumentForm
          formName={formName}
          id={modalState?.id}
          closeModal={onCancel}
        />
      </Modal>
    </>
  )
}
